import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import PatientsList from "./pages/PatientsList";
import PatientDetail from "./pages/PatientDetail";
import CalendarPage from "./pages/CalendarPage";
import InvoicesPage from "./pages/InvoicesPage";
import PublicConsent from "./pages/PublicConsent";
import AppointmentPortal from "./pages/AppointmentPortal";
import ConsentTemplatesPage from "./pages/ConsentTemplatesPage";
import ColleaguesPage from "./pages/ColleaguesPage";
import PatientClaim from "./pages/PatientClaim";
import PublicBooking from "./pages/PublicBookingRedirect";
import PublicTemplateConsent from "./pages/PublicTemplateConsent";
import Pricing from "./pages/Pricing";
import Landing from "./pages/Landing";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionPortal from "./pages/SubscriptionPortal";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";
import AuthCallback from "./pages/AuthCallback";
import PatientRegister from "./pages/PatientRegister";
import ProfessionalsDirectory from "./pages/ProfessionalsDirectory";
import PatientPortalLanding from "./pages/PatientPortalLanding";
import PatientPortal from "./pages/PatientPortal";
import PatientRequests from "./pages/PatientRequests";
import MessagesPage from "./pages/MessagesPage";
import InstallPrompt from "./components/InstallPrompt";
import { useTranslation } from "react-i18next";
import "./App.css";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-stone-500">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" replace />;
  const sub = user?.subscription;
  if (sub && sub.has_access === false && location.pathname !== "/precios") {
    return <Navigate to="/precios" replace />;
  }
  return <Layout>{children}</Layout>;
}

function Home() {
  const { user } = useAuth();
  const { t } = useTranslation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-stone-500">{t("common.loading")}</div>;
  if (!user) return <Landing />;
  const sub = user?.subscription;
  if (sub && sub.has_access === false) return <Navigate to="/precios" replace />;
  return <Layout><Dashboard /></Layout>;
}

function AppRoutes() {
  const location = useLocation();
  // Detect OAuth session_id in URL fragment during render (before ProtectedRoute)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/registro" element={<Register />} />
      <Route path="/consentimiento/:token" element={<PublicConsent />} />
      <Route path="/consentimiento-pdf/:token" element={<PublicTemplateConsent />} />
      <Route path="/cita/:token" element={<AppointmentPortal />} />
      <Route path="/reservar/:doctorId" element={<PublicBooking />} />
      <Route path="/dr/:slug" element={<PatientPortalLanding />} />
      <Route path="/reservation/:slug" element={<PatientPortalLanding />} />
      <Route path="/reservas/:slug" element={<PatientPortalLanding />} />
      <Route path="/patient-claim/:token" element={<PatientClaim />} />
      <Route path="/patient/:slug" element={<PatientPortalLanding />} />
      <Route path="/paciente/:slug" element={<PatientPortalLanding />} />
      <Route path="/paziente/:slug" element={<PatientPortalLanding />} />
      <Route path="/portal" element={<PatientPortalLanding />} />
      <Route path="/registro-paciente" element={<PatientRegister />} />
      <Route path="/profesionales" element={<ProfessionalsDirectory />} />
      <Route path="/mi-portal" element={<PatientPortal />} />
      <Route path="/bienvenido" element={<Landing />} />
      <Route path="/prueba-gratis" element={<Landing />} />
      <Route path="/precios" element={<Pricing />} />
      <Route path="/suscripcion-ok" element={<SubscriptionSuccess />} />
      <Route path="/suscripcion" element={<Protected><SubscriptionPortal /></Protected>} />
      <Route path="/perfil" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/richieste" element={<Protected><PatientRequests /></Protected>} />
      <Route path="/mensajes" element={<Protected><MessagesPage /></Protected>} />
      <Route path="/" element={<Home />} />
      <Route path="/pacientes" element={<Protected><PatientsList /></Protected>} />
      <Route path="/pacientes/:id" element={<Protected><PatientDetail /></Protected>} />
      <Route path="/agenda" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/facturas" element={<Protected><InvoicesPage /></Protected>} />
      <Route path="/plantillas" element={<Protected><ConsentTemplatesPage /></Protected>} />
      <Route path="/colleghi" element={<Protected><ColleaguesPage /></Protected>} />
      <Route path="/colleagues" element={<Protected><ColleaguesPage /></Protected>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <InstallPrompt />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
